import { useDropzone, DropzoneOptions } from "react-dropzone";
import { Grid } from "@mui/material";
import { Close, Error as ErrorIcon } from "@mui/icons-material";
import {
  FileDropzone,
  HelperText,
  StyledErrorMessage,
  UploadedItems,
} from "./styled-components/DropZone";
import { useAppDispatch } from "src/store/store";
import {
  UploadedFileType,
  removeUploadedFile,
  setUploadedFiles,
  uploadedFilesType,
  useOnboarding,
} from "src/store/features/onboarding.slice";
import api from "src/store/features/api";
import { useEffect, useState } from "react";
import ReKycFileUploadBanner from "./ReKycFileUploaadBanner";
import { useUserSlice } from "src/store/features/user.slice";

export const validateFile = (file: uploadedFilesType) => {
  // Convert file size from bytes to megabytes
  const maxFileSize = 50; // 50MB
  const fileSizeInMB = file.size / (1024 * 1024);
  const fileName = file.name.toLowerCase();
  const isValidExtension = /\.(pdf|png|jpeg|mov|mp4|avi|jpg)$/.test(fileName);

  const isValidSize = fileSizeInMB < maxFileSize;

  return [isValidSize, isValidExtension];
};

export const FileUpload = ({ documentType }: { documentType?: "Entity" }) => {
  // Get potential code from url param secure-file-upload adapter
  const code = new URLSearchParams(window.location.search).get("code") || "";

  // Redux
  const dispatch = useAppDispatch();
  const { uploadedFiles, applicationStep, reKycApplicationStep } = useOnboarding();

  // States
  const [error, setError] = useState(false);
  const defaultErrorMessage =
    "There was an issue uploading your document(s), please ensure that they are in the format of PDF/JPEG/JPG/PNG. Maximum file size is 50MB.";
  const [errorMessage, setErrorMessage] = useState(defaultErrorMessage);

  // Queries
  const [uploadFile, { isLoading: isUploading, error: uploadError }] = api.useUploadFileMutation();
  const [deleteFile] = api.useDeleteFileMutation();

  const handleFilesDropped = async (acceptedFiles: File[]) => {
    // Prepare Promises Array
    const uploadPromises = acceptedFiles.map(async (file) => {
      // Prepare form data
      const buffer = await file.arrayBuffer();
      const formData = new FormData();

      formData.append("fileName", "file");
      formData.append("title", `${file.type}`);
      formData.append("versionData", new Blob([buffer]), file.name);
      formData.append("code", code);

      // Upload to the server
      setError(false); // reset error state
      const [isValidSize, isValidExtension] = validateFile(file as uploadedFilesType);
      if (isValidSize && isValidExtension) {
        // Set uploading file state
        let fileData = {
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
          isLoading: true,
          documentType,
        } as uploadedFilesType;

        // Add to the store
        dispatch(setUploadedFiles(fileData));

        const { id } = (await uploadFile({ formData }).unwrap()) as any;

        // update loading state and returned id
        let updatedFileData = { ...fileData, contentVersionId: id, isLoading: false };

        dispatch(setUploadedFiles(updatedFileData));
      } else {
        const prefix = !isValidSize ? "File is too big" : "";
        const sufix = !isValidExtension ? "Invalid file extension" : "";
        setErrorMessage(prefix + " " + sufix);
        setError(true);
        dispatch(
          setUploadedFiles({
            name: "Invalid file",
            size: 0,
            type: "",
            lastModified: 0,
            isError: true,
          } as uploadedFilesType),
        );
      }
    });

    try {
      // Resolve all upload promises
      await Promise.all(uploadPromises);
    } catch (err) {
      setErrorMessage("Something went wrong, please try again.");
      setError(true);
    }
  };

  // Delete from the server and store
  const handleDelete = async (file: { name: string; lastModified: number }) => {
    // Fetch the file from the store by name
    const uploadedFile =
      uploadedFiles.find((uploadedFile) => uploadedFile.name === file.name) || {};

    // Update deleting state
    let updatedFileData = { ...uploadedFile, isDeleting: true };

    dispatch(setUploadedFiles(updatedFileData as uploadedFilesType));

    // Delete from the server
    const { contentVersionId } = uploadedFile as UploadedFileType;

    if (contentVersionId) await deleteFile({ contentVersionId, code });

    // Delete from store
    dispatch(removeUploadedFile({ name: file.name }));

    setError(false);
    setErrorMessage(defaultErrorMessage);
  };

  // @react-dropzone declaration
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleFilesDropped,
  } as DropzoneOptions);

  useEffect(() => {
    if (uploadError?.message) {
      setError(true);
      setErrorMessage(uploadError?.message || "");
    }
  }, [uploadError?.message]);

  const SecureUploadLabel = () => {
    return (
      <ul style={{ maxWidth: "500px", paddingInlineStart: "20px" }}>
        <li>Maximum file size is 50MB</li>
        <li>
          For any proof of address statements e.g., utility bills, please ensure they are issued
          within the last 90 days
        </li>
        <li>
          If uploading an ID, ensure your name and all document details are displayed clearly;
          please avoid sending scanned or photocopied versions of an ID
        </li>
        <li>Your files are in PDF/JPEG/JPG/PNG/MP4/MOV format</li>
        <li>Incompatible formats will be rejected</li>
      </ul>
    );
  };

  const {
    reKycDetails: { reKyc },
  } = useUserSlice();

  const defaultLabel = `Note: Please ensure your document is in PDF/JPEG/JPG/PNG format and dated within the last 90 days. For a driver's license, ensure it's current and displays your address clearly. Maximum file size is 50MB.`;

  // Get current path
  const currentPath = window.location.pathname;

  // Check if currentPath includes secure-file-upload
  const isSecureUpload = currentPath.includes("secure-file-upload");

  const DynamicFileUploadLabel = () => {
    return (
      <Grid item xs={12}>
        {reKyc ? (
          <ReKycFileUploadBanner />
        ) : (
          <HelperText>{isSecureUpload ? <SecureUploadLabel /> : defaultLabel}</HelperText>
        )}
      </Grid>
    );
  };

  return (
    <Grid container spacing={1}>
      <DynamicFileUploadLabel />
      <Grid item xs={12}>
        <FileDropzone {...getRootProps()} error={error}>
          <input {...getInputProps()} />
          <p>Click here to upload.</p>
        </FileDropzone>
      </Grid>

      <Grid item xs={12}>
        {error && (
          <>
            <span style={{ verticalAlign: "text-top", marginRight: "6px" }}>
              <ErrorIcon sx={{ fill: "#FC4C4C", fontSize: "16px" }} />
            </span>
            <StyledErrorMessage>{errorMessage}</StyledErrorMessage>
          </>
        )}
      </Grid>

      {/* Files thumbs */}
      {uploadedFiles
        ?.filter((file) => {
          if (applicationStep === "aboutYourEntity") return file.documentType === "Entity";
          if (reKycApplicationStep === "aboutYourEntity") return file.documentType === "Entity";
          if (applicationStep === "aboutYou") return file.documentType === undefined;
          return true;
        })
        .map((file, it) => {
          return (
            <Grid item xs={12} sm={4} sx={{ position: "relative" }} key={file.name + it}>
              <UploadedItems
                error={file.isError}
                isUploading={file.isLoading}
                isDeleting={file.isDeleting}>
                {file.name}
                {(!isUploading || file.isError) && (
                  <Close
                    sx={{
                      position: "absolute",
                      right: 2,
                      top: 3,
                      fontSize: "20px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleDelete(file)}
                  />
                )}
              </UploadedItems>
            </Grid>
          );
        })}
    </Grid>
  );
};
